import { Tooltip } from '@mantine/core';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  INCIDENTS_API_URL,
  IncidentType,
  useIncidents,
} from '@portals/api/organizations';
import { getDeviceName, RouteModalLink } from '@portals/framework/route-modals';
import { BadgeCell, DateCell, PaginatedTable } from '@portals/table';
import { TableColumn, TableFilterTypeEnum } from '@portals/types';
import {
  getIncidentPriorityLevelColor,
  getIncidentPriorityLevelName,
  PRIORITY_OPTIONS,
} from '@portals/utils';

import { IncidentsTabKeyEnum } from './incidents.types';
import IncidentsRowMenu from './IncidentsRowMenu';
import { IncidentDetailsPanel } from '../../components/Incidents/IncidentDetailsPanel';

const priorityFormatter = (cell, { priority }) => {
  return (
    <BadgeCell
      color={getIncidentPriorityLevelColor(priority)}
      label={getIncidentPriorityLevelName(priority)}
    />
  );
};

const deviceFormatter = (cell, { device_name, device_id }) => (
  <RouteModalLink modalId="device" pathParams={[device_id, 'incidents']}>
    {getDeviceName(device_name)}
  </RouteModalLink>
);

const spaceFormatter = (cell, { space_id, space_tree_path_name }) => (
  <Tooltip label={space_tree_path_name}>
    <Link to={`/overview/${space_id}`}>{space_tree_path_name}</Link>
  </Tooltip>
);

const titleFormatter = (cell, { id, title }) => (
  <Link to={`/incidents/${id}`}>{title}</Link>
);

interface IncidentsListProps {
  defaultFilters: string;
  type: IncidentsTabKeyEnum;
}

export function IncidentsList({ defaultFilters, type }: IncidentsListProps) {
  const columns = useMemo<Array<TableColumn<IncidentType>>>(() => {
    return [
      {
        dataField: 'title',
        text: 'Title',
        sort: true,
        isSticky: true,
        filter: {
          type: TableFilterTypeEnum.Text,
        },
        formatter: titleFormatter,
      },
      {
        dataField: 'priority',
        text: 'Priority',
        sort: true,
        minWidth: 200,
        maxWidth: 200,
        filter: {
          type: TableFilterTypeEnum.Select,
          options: PRIORITY_OPTIONS,
          placeholder: 'Priority...',
        },
        formatter: priorityFormatter,
      },
      {
        dataField: 'device_name',
        text: 'Device',
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Text,
        },
        formatter: deviceFormatter,
      },
      {
        dataField: 'device_model',
        text: 'Model',
        hidden: true,
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Text,
        },
        formatter: (_, { device_model }) => device_model,
      },
      {
        dataField: 'partner_display_name',
        text: 'Vendor',
        hidden: true,
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Text,
        },
      },
      {
        dataField: 'space_tree_path_name',
        text: 'Space',
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Text,
        },
        formatter: spaceFormatter,
      },
      {
        dataField: 'issue',
        text: 'Issue',
        hidden: true,
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Text,
        },
      },
      {
        dataField: 'created_at',
        text: 'Created',
        hidden: true,
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Date,
        },
        formatter: (cell, { created_at }) => (
          <DateCell date={created_at} withTimeAgo={false} />
        ),
      },
      {
        dataField: 'updated_at',
        text: 'Updated',
        hidden: true,
        sort: true,
        filter: {
          type: TableFilterTypeEnum.Date,
        },
        formatter: (cell, { updated_at }) => (
          <DateCell date={updated_at} withTimeAgo={false} />
        ),
      },
      ...(type === IncidentsTabKeyEnum.All
        ? [
            {
              dataField: 'closed_at',
              text: 'Closed',
              hidden: true,
              sort: true,
              filter: {
                type: TableFilterTypeEnum.Date,
              },
              formatter: (cell, { closed_at }) => (
                <DateCell date={closed_at} withTimeAgo={false} />
              ),
            } as const,
          ]
        : []),
      {
        dataField: 'status',
        text: 'Status',
        sort: type === IncidentsTabKeyEnum.All,
        filter: {
          hidden: type === IncidentsTabKeyEnum.Active,
          type: TableFilterTypeEnum.Select,
          options: {
            active: 'Active',
            closed: 'Closed',
          },
        },
        formatter: (_, { status }) => (
          <BadgeCell
            label={status}
            color={status === 'active' ? 'green' : 'blue_gray'}
          />
        ),
      },
    ];
  }, [type]);

  return (
    <PaginatedTable<IncidentType>
      key={type}
      keyField="id"
      columns={columns}
      dataHookUrl={`${INCIDENTS_API_URL}/${defaultFilters}`}
      dataHook={useIncidents}
      name={`organizations.incidents.${type}`}
      noDataIndication={{ title: 'No incidents' }}
      defaultSortBy={[{ id: 'created_at', desc: true }]}
      rowMenu={({ row, wrapperProps }) =>
        row.original.status === 'closed' ? null : (
          <IncidentsRowMenu row={row} wrapperProps={wrapperProps} />
        )
      }
      detailsPanel={{
        type: 'page',
        renderer: ({ row, onClose }) => (
          <IncidentDetailsPanel
            incident={row.original}
            onClosePanel={onClose}
          />
        ),
      }}
    />
  );
}
